import { debounceTime } from "rxjs/operators";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxBaseBlotter, provideBlotter } from "../bfx-base-blotter/bfx-base-blotter";
import {
  ApplicationRef,
  ElementRef,
  Injector,
  NgZone,
  ViewEncapsulation,
  Component,
  Input,
  EventEmitter,
} from "@angular/core";

import { BfxBlotterOrdersService } from "./bfx-blotter-orders.service";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";

import { TranslateService } from "@ngx-translate/core";

import { BfxBlotterFilter } from "../bfx-blotter-filter/bfx-blotter-filter.constants";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";

import { BfxOrderBlotterFilteringService } from "./bfx-blotter-orders-filter.service";
import { BfxBlotterOrdersContextMenuService } from "./bfx-blotter-orders-contextmenu.service";
import {
  OrdersTableDefinitionFactory,
  ProfilePromiseActionService,
  OrderPauseState,
  UserTemperatureService,
} from "shared/cubiq-services";
import { GridOptions, RowNode } from "ag-grid-community/main";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import {
  BfxBlotterAction,
  SystemConfigPromiseService,
  ProfileSettingKeys,
  SysFeatures,
} from "shared/cubiq-services";
import { Temperature } from "shared/cubiq-services";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
import { OrderTypeRenderer } from "./renderers/OrderTypeRenderer";

import {
  COLUMN_DEALT_DIR,
  COLUMN_DEALT_PERCENTAGE,
} from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";

import { BfxUserSettingsService } from "../bfx-user-settings/bfx-user-settings.service";
import { BfxMouseModifierKeyService } from "app/bfx-common/services/bfx-mouse-modifier-key.service";

const FLASH_INTERVAL: number = 1000;
const USER_PREFERENCES_KEY = ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences;
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxSaveProfileNotificationService } from "../bfx-common/services/bfx-save-profile-notification.service";
import { Subscription, Subject } from "rxjs";
import { OrdersTableMode } from "shared/cubiq-services/model/OrdersTableMode";
import { SpotTrackingRenderer } from "./renderers/SpotTrackingRenderer";
import { PercentAwayRenderer } from "./renderers/PercentAwayRenderer";
import { MarketMidRenderer } from "./renderers/MarketMidRenderer";
import { FwdPtsRenderer } from "./renderers/FwdPtsRenderer";

import { LimitOrder } from "shared/cubiq-services/model/orders/LimitOrder";
import { FillRenderer } from "./renderers/FillRenderer";
import { FillPercentRenderer } from "app/bfx-blotter-orders/renderers/FillPercentRenderer";
import { BfxBaseVwapBlotter } from "app/bfx-vwap/bfx-base-vwap-blotter";
import { BfxVwapOrdersService } from "app/bfx-vwap/bfx-vwap-orders.service";
import { BfxVwapInfo } from "app/bfx-vwap/bfx-vwap-info";
import { BfxVwapService } from "app/bfx-vwap/bfx-vwap.service";
import { CommonIntentRenderer } from "app/bfx-blotter-orders/renderers/CommonIntentRenderer";
import { OrderBlotterSavedSettingType } from "app/bfx-user-settings/bfx-order-blotter-settings-preferences.component";

type SelectedStrategyTuple = [boolean, string];
const IS_SELECTED = 0;
const SIBLING_ID = 1;

@Component({
  selector: "bfx-blotter-orders",
  templateUrl: "./bfx-blotter-orders.html",
  encapsulation: ViewEncapsulation.None,
  providers: [provideBlotter(BfxBlotterOrders), BfxBlotterOrdersService],
  viewProviders: [BfxBaseBlotterColumnSelectorService],
  standalone: false,
})
export class BfxBlotterOrders extends BfxBaseVwapBlotter {
  protected showStrategyDescription: boolean = false;
  public showRateControl: boolean = true;
  public showSpotNdfToggle: boolean = false;
  private scheduledColRefresh: boolean = false;

  /** ID of the selected Strategy  - only set when a single order is selected */
  private selectedStrategyMonitor = new SelectedStrategyMonitor();

  protected marketBarOn: boolean = false;
  protected removeMarkeBlotterEmpty: boolean = true;

  protected primed: boolean = false;
  protected scrollToMarket: boolean = false;

  protected firstTimeCheck: boolean = true;
  protected gridBusy: boolean = false;
  protected updateQueue: Set<string> = new Set();
  protected supportsAggregation: boolean = false;
  protected quickFilterAggregation: boolean = false;

  protected flashTimer: any;
  protected temperatureSetting: Temperature = Temperature.Cold;
  public flashRow: boolean = false;
  protected serviceReady: boolean = false;
  private myMode: OrdersTableMode = OrdersTableMode.BASE;
  private myModeSubject: Subject<OrdersTableMode> = new Subject();
  public mode$ = this.myModeSubject.asObservable();

  private subscriptions: Subscription[] = [];

  private rendererSettings: { intentStylingEnabled: boolean };

  public set mode(value: OrdersTableMode) {
    if (value != undefined && value != this.myMode) {
      this.myMode = value;
      if (this.gridOptions) {
        if (this.gridOptions.context != null) {
          this.gridOptions.context.mode = this.mode;
        } else {
          this.gridOptions.context = { ...this.gridOptions.context, ...{ mode: this.myMode } };
        }
        this.gridOptions?.api?.resetQuickFilter();
      }
      this.myModeSubject.next(value);
      // getters need to be re-evaluated
      this.updateGridDataAndTemperature();
    }
  }
  public get mode(): OrdersTableMode {
    return this.myMode;
  }
  public OrdersTableMode: typeof OrdersTableMode = OrdersTableMode;

  public frameworkComponents: any = {
    orderTypeRenderer: OrderTypeRenderer,
    intentRenderer: CommonIntentRenderer,
    spotTrackingRenderer: SpotTrackingRenderer,
    percentAwayRenderer: PercentAwayRenderer,
    marketMidRenderer: MarketMidRenderer,
    fwdPtsRenderer: FwdPtsRenderer,
    fillRenderer: FillRenderer,
    fillPercRenderer: FillPercentRenderer,
  };

  protected get dealtMode(): boolean {
    // don't use in template
    return OrdersTableMode.DEALT === this.mode || OrdersTableMode.NDF_DEALT === this.mode;
  }

  protected set dealtMode(isDealt: boolean) {
    if (isDealt === true && OrdersTableMode.BASE === this.mode) this.mode = OrdersTableMode.DEALT;
    else if (isDealt === true && OrdersTableMode.NDF_BASE === this.mode)
      this.mode = OrdersTableMode.NDF_DEALT;
    else if (OrdersTableMode.DEALT === this.mode) this.mode = OrdersTableMode.BASE;
    else if (OrdersTableMode.NDF_DEALT === this.mode) this.mode = OrdersTableMode.NDF_BASE;
  }

  protected get trackingIsNdf(): boolean {
    // don't use in template
    return OrdersTableMode.NDF_BASE === this.mode || OrdersTableMode.NDF_DEALT === this.mode;
  }

  public isFullTempStyle: boolean = true;

  // at present isRestoredFromConfig exclusively disables restoring
  // this.myMode from table definition default.
  // please expand meaning carefuly as there are subclass interactions
  // in bfx-blotter-inbox -dc
  protected isRestoredFromConfig: boolean = false;
  protected dealtModeInitChange: boolean = false;
  public marketBarEnabled = true;

  @Input()
  public embeddedTableModeSearchDealtToggle = true;
  @Input() public showColumnSelectorStar: boolean = true;

  constructor(
    BfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public service: BfxBlotterOrdersService,
    _contextMenuService: BfxBlotterOrdersContextMenuService,
    zone: NgZone,
    // see _tableDefinitionFactory
    protected factory: OrdersTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    filteringService: BfxOrderBlotterFilteringService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    protected sysConfigService: SystemConfigPromiseService,
    protected userSettingsService: BfxUserSettingsService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    protected userProfileService: BfxUserProfileService,
    protected userTempService: UserTemperatureService,
    protected vwapOrdersService: BfxVwapOrdersService,
    protected vwapInfo: BfxVwapInfo,
  ) {
    super(
      BfxBlotterElement,
      applicationRef,
      injector,
      service,
      _contextMenuService,
      zone,
      factory,
      layoutService,
      translationService,
      blotterFilterService,
      filteringService,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
      vwapInfo,
    );

    if (!this.rendererSettings) {
      this.rendererSettings = { intentStylingEnabled: false };
    }
    this.gridOptions.cacheQuickFilter = true;

    this.profileActionService.getProfileConfig(USER_PREFERENCES_KEY).then((profileValue) => {
      this.mapUserSettings(profileValue);
    });

    this.subscriptions.push(
      this.profileActionService
        .getProfileConfigUpdates(USER_PREFERENCES_KEY)
        .subscribe((profileValue) => {
          this.mapUserSettings(profileValue);
        }),
    );

    this.sysConfigService.getSystemConfig(SysFeatures.ndfEnabled).then(
      (cfg) => {
        this.showSpotNdfToggle =
          cfg == "true" || cfg == "TRUE" || cfg == "1" || cfg == "Y" || cfg == "y";
      },
      (reason) => {
        logger.out.warn(`${this.constructor.name} failed to retrive SysFeatures.ndfEnabled`);
        this.showSpotNdfToggle = false;
      },
    );

    this.subscriptions.push(
      this.userTempService.getUserTempUpdates().subscribe(() => {
        // invalidate temperature filters
        this.gridOptions?.api?.onFilterChanged?.();
      }),
    );
  }

  getVWapService(): BfxVwapService {
    return this.vwapOrdersService;
  }

  getVwapSource(): string {
    return "Order Blotter";
  }

  getVWapUnits() {
    return "orders";
  }
  protected initializeInMemory(): void {
    super.initializeInMemory();
    this.subscriptions.push(
      this.service.partialUpdateSubject.subscribe(this.onPartialUpdateEvent.bind(this)),
    );
  }

  protected getConfigToSaveAsDefault(): any {
    let defColAndSort = super.getConfigToSaveAsDefault();
    defColAndSort["dealtMode"] = this.dealtMode;
    defColAndSort["ndfMode"] = this.trackingIsNdf;
    return defColAndSort;
  }

  protected initializeColumnDefinitions(): void {
    if (!this.isRestoredFromConfig) {
      let defDealtMode = (
        this._tableDefinitionFactory as OrdersTableDefinitionFactory
      ).getDefaultDealtMode();
      let ndfMode = (
        this._tableDefinitionFactory as OrdersTableDefinitionFactory
      ).getDefaultNdfMode();
      if (defDealtMode != undefined) {
        this.dealtMode = defDealtMode;
      }
      if (ndfMode != undefined && ndfMode) {
        this.mode = (this._tableDefinitionFactory as OrdersTableDefinitionFactory).getBlotterMode();
      }
    } else {
    }

    super.initializeColumnDefinitions();
  }

  protected onModelUpdated(): void {
    if (this.marketBarEnabled) {
      this.centerMarketBar();
      this.checkMarketBarOnEmpty();
    }
  }

  private checkMarketBarOnEmpty(): void {
    if (this.gridOptions.api && this.marketBarOn && this.marketBarEnabled) {
      let oldVal = this.removeMarkeBlotterEmpty;
      let rowCount = this.gridOptions.api.getDisplayedRowCount();
      this.removeMarkeBlotterEmpty = rowCount == 0;

      if (oldVal != this.removeMarkeBlotterEmpty) {
        this.filterSelectionChanged(this.filterConfiguration);
      }
    }
  }

  protected checkTemperatureData(params): void {
    try {
      if (
        (params.data.tempStale || params.data.ordTemp == null) &&
        this.tableDefinition &&
        this.tableDefinition.columns
      ) {
        if (this.tableDefinition.columnIndexMap.has(COLUMN_DEALT_PERCENTAGE)) {
          let index: number = this.tableDefinition.columnIndexMap.get(COLUMN_DEALT_PERCENTAGE);
          let column: any = this.tableDefinition.columns[index];
          if (column != null) {
            column.valueGetter(params);
          }
        }
      }
    } catch (e) {
      logger.out.warn("Error while checking for temperature data :" + e);
    }
  }
  /**
   * all acceptance states have a higher precidence than temp on row
   *
   * does not yet apply a css row but could be considered
   * @param params
   */
  protected checkAnyAcceptanceStyle(params): boolean {
    if (params.data) {
      let order = params.data;
      switch (order.orderState) {
        case CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.PENDING_CANCELLATION:
        case CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED:
          return true;
        default:
          return false;
      }
    }
    return false;
  }

  /**
   * pause has a higher precidence than trigger, temperature on row, and
   * primary / jira priority temperature cell styles.
   * @param params
   */
  protected checkPausedStyle(params): boolean {
    return OrderPauseState.isPausedOrderState(params.data);
  }

  protected checkAggregateExpandedStyle(params): boolean {
    return params.data.isLadderAggregate && params.data.aggregateExpanded;
  }

  protected checkPrimaryPausedStyle(params): boolean {
    return (
      params.data != null &&
      params.data.pauseState != null &&
      CubiqTS.PauseState.PAUSED === params.data.pauseState
    );
  }
  protected checkPrimaryHotStyle(params): boolean {
    if (this.checkPrimaryPausedStyle(params)) return false;

    this.checkTemperatureData(params);
    return Temperature.Hot == params.data.ordTemp;
  }
  protected checkPrimaryWarmStyle(params): boolean {
    if (this.checkPrimaryPausedStyle(params)) return false;

    this.checkTemperatureData(params);
    return Temperature.Warm == params.data.ordTemp;
  }
  protected checkPrimaryColdStyle(params): boolean {
    if (this.checkPrimaryPausedStyle(params)) return false;

    return Temperature.Cold == params.data.ordTemp;
  }

  protected checkHotStyle(params): boolean {
    if (this.isFullTempStyle && this.isTempOnRow(params)) {
      this.checkTemperatureData(params);
      return Temperature.Hot == params.data.ordTemp;
    }
    return false;
  }
  protected checkWarmStyle(params): boolean {
    if (this.isFullTempStyle && this.isTempOnRow(params)) {
      this.checkTemperatureData(params);
      return Temperature.Warm == params.data.ordTemp;
    }
  }
  protected checkColdStyle(params): boolean {
    if (this.isFullTempStyle && this.isTempOnRow(params)) {
      return Temperature.Cold == params.data.ordTemp;
    }
  }

  protected checkTriggeredStyle(params): boolean {
    if (this.checkPausedStyle(params)) return false;
    return CubiqTS.OrderState.TRIGGERED === params.data.orderState;
  }

  protected isTempOnRow(params) {
    if (this.checkPausedStyle(params)) return false;
    if (this.checkAggregateExpandedStyle(params)) return false;

    if (this.checkAnyAcceptanceStyle(params)) return false;

    if (params.data != null) {
      let order = params.data;
      switch (order.orderState) {
        case CubiqTS.OrderState.TRIGGERED:
        case CubiqTS.OrderState.SUSPENDED:
        case CubiqTS.OrderState.PENDING:
          return false;
        case CubiqTS.OrderState.ACTIVE:
        case CubiqTS.OrderState.IN_MODIFICATION:
        default:
          return true;
      }
    }

    return true;
  }

  protected checkExternalStyle(params): boolean {
    return (
      CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED == params.data.orderState ||
      CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED == params.data.orderState ||
      CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED == params.data.orderState
    );
  }

  /**
   * Style check for strategy icon - only true if there is a select strategy wit hthe same ref
   * @param params
   * @returns
   */
  private rowClassRules(params): boolean {
    return this.selectedStrategyMonitor.isHighlighted(params.data);
  }

  /**
   * Redraw any rows that have an order with the specified strategy Ref - used for adding or removing strategy icon highlights
   * @param strategyId
   */
  private redrawRowsWithStrategyRef(strategyRef: String): void {
    let rows = new Array<RowNode>();
    const fromRow = this.gridOptions.api.getFirstDisplayedRow();
    const toRow = this.gridOptions.api.getLastDisplayedRow();

    for (let i = fromRow; i <= toRow; i++) {
      let rowData = this.gridOptions.api.getDisplayedRowAtIndex(i);
      if (rowData && rowData.data && rowData.data.strategyRef == strategyRef) {
        rows.push(rowData);
      }
    }
    if (rows.length >= 1) {
      this.gridOptions.api.redrawRows({ rowNodes: rows });
    }
  }

  protected override initGridOptionsContext(): any {
    let retContext = super.initGridOptionsContext();
    // Add a blotterSettings - this is called from constructor so do init here rather than as property initialiser
    this.rendererSettings = { intentStylingEnabled: false };
    retContext.rendererSettings = this.rendererSettings;

    if (this.myMode == undefined) {
      this.myMode = OrdersTableMode.BASE;
    }
    retContext.mode = this.myMode;
    return retContext;
  }

  protected createsGridOptions(): GridOptions {
    let go = super.createsGridOptions();

    go.onSortChanged = this.checkIfNeedMarketBar.bind(this);
    go.onRowSelected = (event) => {
      this.onRowSelected(event);
    };

    if (!this.embeddedTableMode) {
      if (this.showRowColors == true) {
        go.rowClassRules = {
          "strategy-highlight": this.rowClassRules.bind(this),
          "ladder-rate": function (params) {
            return params.data.isLadderRate;
          },
          hot: this.checkHotStyle.bind(this),
          warm: this.checkWarmStyle.bind(this),
          cold: this.checkColdStyle.bind(this),
          paused: this.checkPausedStyle.bind(this),
          aggregate_expanded: this.checkAggregateExpandedStyle.bind(this),
          "percent-away-temp-hot": this.checkPrimaryHotStyle.bind(this),
          "percent-away-temp-warm": this.checkPrimaryWarmStyle.bind(this),
          "percent-away-temp-cold": this.checkPrimaryColdStyle.bind(this),
          triggered: this.checkTriggeredStyle.bind(this),
          cancel_acceptance_needed: function (params) {
            return CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED == params.data.orderState;
          },
          modify_acceptance_needed: function (params) {
            return CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED == params.data.orderState;
          },
          expiry_acceptance_needed: function (params) {
            return CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED == params.data.orderState;
          },
          trigger_expiry_acceptance_needed: function (params) {
            return CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED == params.data.orderState;
          },
          acceptance_needed: function (params) {
            return CubiqTS.OrderState.ACCEPTANCE_NEEDED == params.data.orderState;
          },
          suspended: function (params) {
            return CubiqTS.OrderState.SUSPENDED === params.data.orderState;
          },
          pending_cancellation: function (params) {
            return CubiqTS.OrderState.PENDING_CANCELLATION == params.data.orderState;
          },
          pending: function (params) {
            return CubiqTS.OrderState.PENDING == params.data.orderState;
          },
          migration_acceptance_needed: function (params) {
            return CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED == params.data.orderState;
          },
          fwd_pending: function (params) {
            return (
              CubiqTS.OrderState.PENDING_FWDS == params.data.orderState ||
              CubiqTS.OrderState.EXPIRY_PENDING_FWDS == params.data.orderState ||
              CubiqTS.OrderState.CANCEL_PENDING_FWDS == params.data.orderState
            );
          },
          external: this.checkExternalStyle.bind(this),
          rowFlash: function (params) {
            return params.data.rowFlash;
          },
        };
      } else {
        go.rowClassRules = {};
      }
    }

    go.onModelUpdated = this.onModelUpdated.bind(this);
    return go;
  }

  protected centerMarketBar(): void {
    if (
      this.gridOptions.api &&
      this.marketBarOn &&
      this.service.marketBar &&
      this.scrollToMarket &&
      this.tableDefinition != null
    ) {
      let marketNode = this.gridOptions.api.getRowNode(
        this.service.marketBar[this.tableDefinition.primaryKey],
      );
      if (marketNode) {
        // this.scrollToMarket = false;
        this.gridOptions.api.ensureNodeVisible(marketNode, "middle");
      }
    }
  }

  protected onRowSelected(event, selectedNodes?: RowNode[]) {
    // Only highlight sibling orders when a single node is selected in the ladder
    selectedNodes = selectedNodes ?? this.gridOptions.api.getSelectedNodes();

    this.selectedStrategyMonitor.setSelectedRows(selectedNodes);
    if (this.selectedStrategyMonitor.lastStrategyRef) {
      this.redrawRowsWithStrategyRef(this.selectedStrategyMonitor.lastStrategyRef);
    }
    if (this.selectedStrategyMonitor.selectedStrategyRef) {
      this.redrawRowsWithStrategyRef(this.selectedStrategyMonitor.selectedStrategyRef);
    }
  }

  protected onDeleteEvent(rowId: string): void {
    this.removeFromSets(rowId);
    super.onDeleteEvent(rowId);
  }

  private removeFromSets(orderId: string): void {
    if (this.updateQueue) {
      if (this.updateQueue.has(orderId)) {
        this.updateQueue.delete(orderId);
      }
    }
  }

  public showSettings(event: Event): void {
    this.layoutService.showSettings("Order", event);
  }

  protected blotterReady(): void {
    this.serviceReady = true;
    this.flashTimer = setInterval(this.updateFlashRow.bind(this), FLASH_INTERVAL);
  }

  public onMouseWheel(event: MouseEvent) {
    this.scrollToMarket = false;
  }

  public onMouseDown(event) {
    if (this.inScrollRange(event)) {
      this.scrollToMarket = false;
    }
  }

  public onKeyPress(event) {
    super.onKeyPress(event);
    if (
      event.key == "ArrowUp" ||
      event.key == "ArrowDown" ||
      event.key == "PageUp" ||
      event.key == "PageDown" ||
      event.key == "Home" ||
      event.key == "End"
    ) {
      this.scrollToMarket = false;
    }
  }

  protected inScrollRange(event): boolean {
    let e = event.target;
    if (e && e.className == "ag-body-viewport") {
      if (event.clientX >= e.offsetWidth) {
        return true;
      }
    }
    return false;
  }

  protected onPartialUpdateEvent(rows: Array<any>): void {
    if (this.gridBusy) {
      for (let i = 0, len = rows.length; i < len; i++) {
        if (!this.updateQueue.has(rows[i][this.tableDefinition.primaryKey])) {
          this.updateQueue.add(rows[i][this.tableDefinition.primaryKey]);
        }
      }
    } else {
      if (this.gridOptions.api) {
        if (!this.areUpdatesFrozen()) {
          this.gridBusy = true;
          this.gridOptions.api.batchUpdateRowData({ update: rows }, (transaction) => {
            this.processedRateUpdate(transaction);
          });
        }
      }
    }
  }

  protected checkQueue() {
    if (!this.scheduledRefresh) {
      let updates = this.service.getRowDataForIds(this.updateQueue);
      this.updateQueue.clear();
      this.gridBusy = false;
      if (updates.length > 0) {
        this.onPartialUpdateEvent(updates);
      }
    } else {
      setTimeout(this.checkQueue.bind(this), 500);
    }
  }

  protected processedRateUpdate(transaction: any): void {
    setTimeout(this.checkQueue.bind(this), 1000);
    this.centerMarketBar();
  }

  protected checkIfNeedMarketBar(): void {
    if (
      this.gridOptions != null &&
      this.gridOptions.api &&
      this.tableDefinition != null &&
      this.service.marketBar != null
    ) {
      let sort: Array<any> = this.gridOptions.api.getSortModel();
      let wasOn: boolean = this.marketBarOn;
      let marketNode = this.gridOptions.api.getRowNode(
        this.service.marketBar[this.tableDefinition.primaryKey],
      );
      if (marketNode && this.marketBarEnabled) {
        if (sort && sort.length > 0) {
          let isOn: boolean =
            sort[0].colId == "dealtPercentAway" || sort[0].colId == "basePercentAway";
          this.marketBarOn = isOn;
          this.scrollToMarket = isOn;

          if (wasOn != isOn) {
            let selectMarketBar = isOn && this.isAllSelected();
            this.gridOptions.suppressMultiSort = isOn;
            // third argument true preserves selectionController.lastSelectedNode
            marketNode.setSelected(selectMarketBar, false, true);
            this.filterSelectionChanged(this.filterConfiguration);
          }
        } else {
          this.marketBarOn = false;
          if (wasOn || this.firstTimeCheck) {
            this.gridOptions.suppressMultiSort = false;
            this.firstTimeCheck = false;
            // third argument true preserves selectionController.lastSelectedNode
            marketNode.setSelected(false, false, true);
            this.filterSelectionChanged(this.filterConfiguration);
          }
        }
      } else {
        if (
          sort &&
          sort.length > 0 &&
          (sort[0].colId == "dealtPercentAway" || sort[0].colId == "basePercentAway")
        ) {
          this.gridOptions.api.ensureIndexVisible(0, "top");
        }
      }
    }
  }

  public doesExternalFilterPass(node: RowNode): boolean {
    let visible: boolean = this.checkExternalFilterVisibility(node);

    if (!visible && node.isSelected() && this.gridOptions.api) {
      // third argument true preserves selectionController.lastSelectedNode
      node.setSelected(false, false, true);
      // this.gridOptions.api.deselectNode(node);
    }

    return visible;
  }

  public doesTemperatureFilterPass(blotterTemperature: Temperature, rawData: LimitOrder): boolean {
    let rowTemperature: Temperature = rawData.ordTemp;

    if (this.dealtMode) {
      rowTemperature = this.factory.dealtPercentAwayHelper.getTemperature(rawData);
    } else {
      rowTemperature = this.factory.basePercentAwayHelper.getTemperature(rawData);
    }

    return rowTemperature != null ? rowTemperature >= blotterTemperature : true;
  }

  protected checkExternalFilterVisibility(node: RowNode): boolean {
    if (node == null) return false;

    if (this.service.marketBar) {
      if (
        node.data[this.tableDefinition.primaryKey] ==
        this.service.marketBar[this.tableDefinition.primaryKey]
      ) {
        return this.marketBarOn && !this.removeMarkeBlotterEmpty;
      }
    }
    if (
      this.serviceReady &&
      this.shouldShowTemperatureFilter() &&
      this.temperatureSetting > Temperature.Cold
    ) {
      if (!this.doesTemperatureFilterPass(this.temperatureSetting, node.data)) {
        return false;
      }
    }
    if (node.data["isBlotterOrder"] == false) {
      return false;
    }

    let filters: Array<BfxBlotterFilter> = this.blotterFilterBar?.getSelectedFilters();

    try {
      if (filters != null && filters.length > 0) {
        return this.filteringService.doFiltersPass(node, filters, false, this.dealtMode);
      }
    } catch (error) {
      logger.out.error("Error while doing a filter pass.", error);
    }
    return true;
  }

  protected getBlotterVersionIdentifier(): string {
    return "1.3";
  }

  protected onAdaptedDataPrimed(): void {
    this.primed = true;
    this.checkIfNeedMarketBar();
  }

  protected checkForNoRows(): void {}

  public override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.flashTimer != null) {
      clearInterval(this.flashTimer);
    }
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  protected updateFlashRow(): void {
    this.flashRow = !this.flashRow;
  }

  public onCellDoubleClicked(event) {
    // we need this to be the orderId - as the node Id may not be the orderId
    if (event && event.node && event.node.data) {
      let orderId = event.node.data.orderId.toString();
      if (orderId) {
        this.contextMenuService.fireActionEvent(
          BfxBlotterAction.ACTION_VIEW_DETAILS,
          this,
          [orderId],
          [event.node.data],
          null,
          null,
          null,
          event,
        );
      }
    }
  }

  public pauseRates(): void {
    this.service.pauseRates();
  }

  public resumeRates(): void {
    this.service.resumeRates();
  }

  public get ratesPaused(): boolean {
    return this.service.areRatesPaused();
  }

  public getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.ORDER;
  }

  public isExternalFilterPresent(): boolean {
    return true;
  }

  public getTemperatureSetting(): Temperature {
    return this.temperatureSetting;
  }

  public shouldShowTemperatureFilter(): boolean {
    return true;
  }

  /**
   * Invoked by a temperature filter component when it's value changes
   * @param temperature the current temperature selection
   */
  public temperatureSelectionChanged(temperature: Temperature): void {
    this.temperatureSetting = temperature;
    if (this.gridOptions.api) {
      this.gridOptions.api.onFilterChanged();
      this.afterFilteringCompleted();
      this.centerMarketBar();
    }
  }

  /**
   * Override to include order blotter specific settings
   */
  public getComponentConfiguration(): any {
    let configuration: any = super.getComponentConfiguration();
    configuration.temperatureSetting = this.temperatureSetting;
    configuration.dealtMode = this.dealtMode;
    configuration.ndfMode = this.trackingIsNdf;
    return configuration;
  }

  /**
   * Override to include order blotter specific settings
   * @param config blotter component configuration
   */
  public restoreFromConfiguration(config: any): void {
    //	if(this.getBlotterVersion()==config.configVersion){
    this.isRestoredFromConfig = true;
    if (config != null && config.temperatureSetting != null) {
      this.temperatureSelectionChanged(config.temperatureSetting);
    }

    if (this.dealtMode != config.dealtMode) {
      this.dealtMode = config.dealtMode;
      this.dealtModeInitChange = true;
    }
    if (config.ndfMode != undefined && config.ndfMode) {
      this.mode = this.dealtMode ? OrdersTableMode.NDF_DEALT : OrdersTableMode.NDF_BASE;
    }
    //	}

    super.restoreFromConfiguration(config);
  }

  protected doResortOnPartialUpdate(): boolean {
    return this.marketBarOn;
  }

  protected selectedRowsLength(): number {
    let marketNodeSelected: boolean = false;
    if (this.service.marketBar != null) {
      let marketNode = this.gridOptions.api.getRowNode(
        this.service.marketBar[this.tableDefinition.primaryKey],
      );
      if (this.marketBarOn && marketNode) {
        marketNodeSelected = marketNode.isSelected();
      }
    }
    return this.gridOptions.api.getSelectedRows().length - (marketNodeSelected ? 1 : 0);
  }

  public switchMode(mode: OrdersTableMode): void {
    this.mode = mode;
    this.updateGridData();
    this.filterSelectionChanged(this.filterConfiguration);
    this.gridOptions.api.redrawRows();
  }

  public applySwapMap(swapMap: Map<string, string>): void {
    let sortModelInvalidated = true;
    let defColIdIndexMap = new Map<string, number>();
    let colIdIndexMap = new Map<string, number>();
    let colState = this.gridOptions.columnApi.getColumnState();
    let sortModel = this.gridOptions.api.getSortModel();
    colState.forEach((col, i) => {
      colIdIndexMap.set(col.colId, i);
    });
    this.gridOptions.columnDefs.forEach((col, i) => {
      defColIdIndexMap.set(col["field"], i);
    });

    for (const kv of swapMap.entries()) {
      const toHideId = kv[0];
      const toShowId = kv[1];
      const toHideIndex = colIdIndexMap.get(toHideId);
      const toShowIndex = colIdIndexMap.get(toShowId);
      const toHide = colState[toHideIndex];
      const toShow = colState[toShowIndex];
      if (toHide && toShow) {
        // set up swap values
        const toShowWidth = toHide["width"];
        const toShowVisible = !toHide["hide"];
        const toHideWidth = toShow["width"];
        const toHideVisible = !toShow["hide"];

        this.columnDefs[defColIdIndexMap.get(toHideId)].omitFromPicker = true;
        this.columnDefs[defColIdIndexMap.get(toShowId)].omitFromPicker = false;
        this.columnDefs[defColIdIndexMap.get(toShowId)].hide =
          this.columnDefs[defColIdIndexMap.get(toHideId)].hide;
        this.columnDefs[defColIdIndexMap.get(toHideId)].hide = true;
        // set new sizes, then move into place & flip visibility
        this.gridOptions.columnApi.setColumnWidth(toShowId, toShowWidth, false);
        if (toShowIndex < toHideIndex) {
          this.gridOptions.columnApi.moveColumnByIndex(toShowIndex, toHideIndex - 1);
          this.gridOptions.columnApi.moveColumnByIndex(toHideIndex, toShowIndex);
        } else {
          this.gridOptions.columnApi.moveColumnByIndex(toShowIndex, toHideIndex);
        }
        this.gridOptions.columnApi.setColumnVisible(toShowId, toShowVisible);

        // this avoids reseting scroll position at end
        this.gridOptions.columnApi.setColumnWidth(toHideId, toHideWidth);
        this.gridOptions.columnApi.setColumnVisible(toHideId, toHideVisible);

        // update sorting model if needed
        if (sortModel) {
          sortModel.forEach((sortEntry, i, sortModel) => {
            if (sortEntry.colId === toHideId) {
              sortEntry.colId = toShowId;
              sortModelInvalidated = true;
            }

            sortModel[i] = sortEntry;
          });
        }

        if (sortModelInvalidated) this.gridOptions.api.setSortModel(sortModel);
      }
    }
  }

  public switchDealtToBase(): void {
    if (this.dealtMode) {
      this.toggleBaseDealt();
    }
  }

  public switchBaseToDealt(): void {
    if (!this.dealtMode) {
      this.toggleBaseDealt();
    }
  }

  public toggleBaseDealt(): void {
    let mode: OrdersTableMode;

    switch (this.mode) {
      case OrdersTableMode.BASE:
        mode = OrdersTableMode.DEALT;
        break;
      case OrdersTableMode.DEALT:
        mode = OrdersTableMode.BASE;
        break;
      case OrdersTableMode.NDF_BASE:
        mode = OrdersTableMode.NDF_DEALT;
        break;
      case OrdersTableMode.NDF_DEALT:
        mode = OrdersTableMode.NDF_BASE;
        break;
      default:
        mode = OrdersTableMode.BASE;
        break;
    }

    this.switchMode(mode);
  }

  public toggleNdfSpotTracking(): void {
    let mode: OrdersTableMode;

    switch (this.mode) {
      case OrdersTableMode.BASE:
        mode = OrdersTableMode.NDF_BASE;
        break;
      case OrdersTableMode.DEALT:
        mode = OrdersTableMode.NDF_DEALT;
        break;
      case OrdersTableMode.NDF_BASE:
        mode = OrdersTableMode.BASE;
        break;
      case OrdersTableMode.NDF_DEALT:
        mode = OrdersTableMode.DEALT;
        break;
      default:
        mode = OrdersTableMode.BASE;
        break;
    }

    this.switchMode(mode);
  }

  /**
   * Override on default behaviour.
   * Rows with ids < 0 are considered invalid.
   */
  protected isValidRowID(rowId: string): boolean {
    return Number(rowId) >= 0;
  }

  private mapUserSettings(profileValue: any) {
    try {
      let savedSettings = JSON.parse(profileValue);
      if (savedSettings != undefined && savedSettings != null) {
        const intentStylingEnabled = savedSettings.intentStylingEnabled === "y";
        this.isFullTempStyle =
          savedSettings.hasOwnProperty("tempColorFullModel") &&
          savedSettings.tempColorFullModel !== null &&
          savedSettings.tempColorFullModel == "y"
            ? true
            : false;
        this.marketBarEnabled =
          savedSettings.hasOwnProperty("marketBar") &&
          savedSettings.marketBar !== null &&
          savedSettings.marketBar == "y"
            ? true
            : false;
        this.postUserSettingsRefresh();
        if (intentStylingEnabled != this.rendererSettings.intentStylingEnabled) {
          this.rendererSettings.intentStylingEnabled = intentStylingEnabled;
          this.refreshRendererSettings();
        }
      }
    } catch (e) {
      logger.out.warn(
        "Failed to parse profile value for key ",
        ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences,
        " with value: ",
        profileValue,
        e,
      );
    }
  }

  private refreshRendererSettings() {
    setTimeout(() => {
      logger.out.info("BlotterOrders: Refreshing intent col");
      this.gridOptions.api.refreshCells({
        columns: [COLUMN_DEALT_DIR],
        force: true,
      });
    }, 100);
  }

  protected postUserSettingsRefresh() {
    setTimeout(() => {
      this.updateMarketBarStatus();
      if (this.tableDefinition != null) {
        try {
          logger.out.info("Performing full refresh ... ");
          this.onPartialUpdateEvent(this.service.getRowData());
        } catch (e) {
          logger.out.error("Failed to perform full refresh " + e);
        }
      }
    }, 500);
  }

  public subscribeToSearch(): void {
    this.search = new EventEmitter<string>();
    this.sysConfigService.getSystemConfig(SysFeatures.searchDelay).then((config: string) => {
      this.search.pipe(debounceTime(Number(config))).subscribe(() => {
        this.updateSearch();
      });
    });
  }

  protected updateSearch() {
    if (this.gridOptions.api && this.marketBarOn) {
      let marketNode = this.gridOptions.api.getRowNode(
        this.service.marketBar[this.tableDefinition.primaryKey],
      );
      if (marketNode) {
        marketNode.data["as_search_dealtPercentAway"] = this.searchValue;
      }
    }
    this.gridOptions.api.setQuickFilter(this.searchValue);
    this.checkIfNeedMarketBar();
    this.searchComplete();
  }

  protected searchComplete(): void {}

  protected _agGridResetScroll(): void {
    super._agGridResetScroll();
    this.centerMarketBar();
  }

  private updateMarketBarStatus() {
    if (this.marketBarEnabled) {
      this.checkIfNeedMarketBar();
    } else {
      this.marketBarOn = false;
    }
    this.applySortingOnMarketBarStatusUpdate();
  }

  private applySortingOnMarketBarStatusUpdate() {
    if (this.gridOptions.api && this.tableDefinition) {
      let baseCol = this.gridOptions.api.getColumnDef("basePercentAway");
      if (baseCol) {
        baseCol.sortingOrder = this.marketBarEnabled ? ["asc", null] : ["asc", "desc", null];
      }
      let dealtCol = this.gridOptions.api.getColumnDef("dealtPercentAway");
      if (dealtCol) {
        dealtCol.sortingOrder = this.marketBarEnabled ? ["asc", null] : ["asc", "desc", null];
      }
      let sort: Array<any> = this.gridOptions.api.getSortModel();
      if (
        sort.length > 0 &&
        (sort[0].colId == "dealtPercentAway" || sort[0].colId == "basePercentAway") &&
        this.marketBarEnabled
      ) {
        let sortCol = this.gridOptions.columnApi.getColumn(sort[0].colId);
        if (sortCol) {
          sortCol.setSort("asc");
        }
      }
      if (!this.marketBarEnabled) this.gridOptions.api.ensureIndexVisible(0, "top");
    }
  }

  protected updateGridDataAndTemperature(): void {
    // if(this.scheduledRefresh){
    // 	this.scheduledRefresh=false;
    // }

    // this.scheduledRefresh=true;
    // setTimeout(() => {
    if (this.tableDefinition && this.gridOptions && this.gridOptions.api) {
      this.gridOptions.api.setRowData(this.service.getRowDataWithStaleTemp());
      this.gridDataUpdated();
    }
    // this.scheduledRefresh=false;
    // 	}, 200);
    // }
  }

  protected onFullUpdateEvent(rows: Array<any>): void {
    super.onFullUpdateEvent(rows);

    try {
      if (
        rows != null &&
        rows.length > 0 &&
        this.searchValue != null &&
        this.searchValue.length > 0
      ) {
        rows
          .map((row: any) => this.gridOptions.getRowNodeId(row))
          .forEach((id: any) => {
            let rowNode: RowNode = this.gridOptions.api.getRowNode(id);
            if (rowNode != null) {
              rowNode.resetQuickFilterAggregateText();
            }
          });
      }
    } catch (e) {
      logger.out.warn(
        this.constructor.name + ": Failed to clear quick filter data on order blotter " + e,
      );
    }
  }

  public getTabIdentifier(): string {
    return "BfxBlotterOrders";
  }
}

class SelectedStrategyMonitor {
  /** ID of last selected strategy when a new strategy is selected, or one just cleared */
  public lastStrategyRef: string;
  /** ID of current selected strategy */
  public selectedStrategyRef: string;
  private selectedOcoId: string;

  /**
   * Is the specified order to be shown as highlighted?
   * @param order
   * @returns
   */
  public isHighlighted(order: CubiqTS.OrderDataImpl): boolean {
    return (
      order.siblingRelationship &&
      order.strategyRef == this.selectedStrategyRef &&
      (order.strategyType == CubiqTS.StrategyType.GAMMA ||
        order.orderRef == this.selectedOcoId ||
        order.siblingRelationship.orderRef == this.selectedOcoId)
    );
  }

  /**
   * Set the currently selected rows. This will update the kastSelectedRef and
   * @param rows
   * @returns true if selected strategy has changed
   */
  setSelectedRows(rows: Array<{ data: CubiqTS.OrderDataImpl }>): boolean {
    if (rows.length == 0) {
      return this.clearStrategy();
    }

    const order = rows[0].data;
    if (!order.siblingRelationship) {
      return this.clearStrategy();
    }
    if (rows.length == 1) {
      this.setSelectedStrategy(order);
      return true;
    } else if (this.inSameStrategy(order, rows)) {
      this.setSelectedStrategy(order);
      return true;
    } else {
      return this.clearStrategy();
    }
  }

  /**
   * Are all orders in rows array members siblings
   * @param order
   * @param rows
   * @returns
   */
  private inSameStrategy(order, rows: Array<{ data: CubiqTS.OrderDataImpl }>): boolean {
    const strategyRef = order.strategyRef;

    return (
      // Same GAMMA check - order is a Gamma, and all other rows have same strategy
      (order.strategyType == CubiqTS.StrategyType.GAMMA &&
        !rows.some((value) => value.data.strategyRef != strategyRef)) || // Same OCO check - order is OCO, there is two orders and second order is sibling of first order
      (order.siblingRelationship.relationshipType == CubiqTS.OrderRelationshipType.OCO &&
        rows.length == 2 &&
        rows[1].data.siblingRelationship &&
        rows[1].data.siblingRelationship.orderRef == order.orderRef)
    );
  }

  /**
   * Set the specified order as the currently seleelcted strategy
   * @param order
   */
  private setSelectedStrategy(order: CubiqTS.OrderDataImpl): void {
    this.lastStrategyRef = this.selectedStrategyRef;
    this.selectedStrategyRef = order.strategyRef;
    this.selectedOcoId = order.orderRef;
  }

  /**
   * Ensure highlighted strategy is cleared
   * @returns true if strategy has been cleared
   */
  private clearStrategy(): boolean {
    const oldRef = this.selectedStrategyRef;
    this.lastStrategyRef = oldRef;

    if (oldRef) {
      this.selectedStrategyRef = null;
      this.selectedOcoId = null;
      return true;
    }
    return false;
  }
}
